import { useCallback } from 'react';
import { ListPhoneNumbersForLocationsResponse } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-numbers/phone_numbers_service.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { useQueryClient } from 'react-query';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

/**
 * Helper to format a protobuf phone number (from cache) to E164 format.
 */
const formatProtobufPhoneToE164 = (phone: { nationalNumber?: string | number; countryCode?: number }): string => {
  const nationalNumber = phone.nationalNumber;
  if (nationalNumber === undefined) {
    return '';
  }
  // nationalNumber can be a string or number, convert to string
  const nationalNumberStr = String(nationalNumber);
  if (!nationalNumberStr) {
    return '';
  }
  // formatPhoneNumberE164 handles adding country code
  return formatPhoneNumberE164(nationalNumberStr);
};

/**
 * Helper function to update the defaultAi flag in phone numbers cache.
 * Sets defaultAi=true for all numbers present in `newPhoneNumbers` array;
 * sets defaultAi=false for other numbers that previously had it set.
 */
const updatePhoneNumbersCache = (
  data: ListPhoneNumbersForLocationsResponse | undefined,
  newPhoneNumbers: string[] | undefined
): ListPhoneNumbersForLocationsResponse | undefined => {
  if (!data?.phoneNumbers) return data;
  if (!newPhoneNumbers || newPhoneNumbers.length === 0) {
    // If no numbers provided, clear any existing defaultAi flags
    return {
      ...data,
      phoneNumbers: data.phoneNumbers.map((phone) => (phone.defaultAi ? { ...phone, defaultAi: false } : phone)),
    };
  }

  const formattedPhoneTargets = new Set(newPhoneNumbers.map((n) => formatPhoneNumberE164(n)));

  return {
    ...data,
    phoneNumbers: data.phoneNumbers.map((phone) => {
      const phoneE164 = phone.phoneNumber ? formatProtobufPhoneToE164(phone.phoneNumber) : '';

      if (formattedPhoneTargets.has(phoneE164)) {
        return { ...phone, defaultAi: true };
      }

      // Clear defaultAi for numbers not in the updated list
      if (phone.defaultAi) {
        return { ...phone, defaultAi: false };
      }
      return phone;
    }),
  };
};

/**
 * A hook that returns a function to execute when an INBOX_AI_RECEPTIONIST_NUMBER_UPDATE event is received.
 * This event is triggered when the AI configuration for a phone number changes on a location.
 * It updates the phone numbers cache to reflect the new AI configuration.
 */
export const useInboxPhoneConfigChangeWSHandler = () => {
  const queryClient = useQueryClient();
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const fusionPhaseThreeEnabled = getFeatureFlagValue('nwx:fusion-phase-three', 'ALL');

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.INBOX_AI_RECEPTIONIST_NUMBER_UPDATE>>(
    async (event) => {
      if (!fusionPhaseThreeEnabled) {
        return;
      }

      const { params } = event;
      // ignore any `eventType` in params; only use locationId and the new phone numbers array
      const { locationId, newPhoneNumbers } = params as { locationId: string; newPhoneNumbers?: string[] };

      // Only process events for locations the user has selected
      if (!selectedLocationIds.includes(locationId)) {
        return;
      }

      // Optimistically update cached phone data so the UI reacts instantly
      queryClient.setQueriesData<ListPhoneNumbersForLocationsResponse | undefined>(
        { queryKey: [PhoneNumbersV1.Service.serviceName, 'ListPhoneNumbersForLocations'], exact: false },
        (oldData) => updatePhoneNumbersCache(oldData, newPhoneNumbers)
      );

      queryClient.setQueriesData<ListPhoneNumbersForLocationsResponse | undefined>(
        { queryKey: [PhoneNumbersV1.Service.serviceName, 'ListUserAccessiblePhoneNumbers'], exact: false },
        (oldData) => updatePhoneNumbersCache(oldData, newPhoneNumbers)
      );

      // phones setting page for AI config invalidates: ListPhoneNumbersForLocations

      // Immediately refetch to pick up authoritative data (names)
      queryClient.invalidateQueries({
        queryKey: [PhoneNumbersV1.Service.serviceName, 'ListUserAccessiblePhoneNumbers'],
        exact: false,
      });
    },
    [fusionPhaseThreeEnabled, selectedLocationIds, queryClient]
  );

  return wsEventHandler;
};

export const useInboxPhoneConfigChangeWSSubscription = () => {
  const handler = useInboxPhoneConfigChangeWSHandler();
  return useWebsocketEventSubscription(NotificationType.INBOX_AI_RECEPTIONIST_NUMBER_UPDATE, handler);
};
